import { z } from 'zod'
import type {
  LiveActivityKind,
  LiveActivityRegisterBody,
  LiveActivityStartTokenBody,
} from '../live-activity'

/**
 * 실시간 현황 토큰 경로의 입력 검증 (모양은 live-activity.ts 공통 약속). 앱이 ActivityKit 에서 받은 토큰을 서버에 알린다.
 * - POST /live-activities: 진행 중인 실시간 현황 하나의 pushToken
 * - PUT /live-activities/start-token: 앱이 꺼져 있어도 서버가 시작할 수 있는 pushToStartToken (iOS 17.2~)
 * - DELETE /live-activities/:kind/:refId: 앱에서 끝냈다 — 서버는 더 보내지 않는다
 * 시식·조리 타이머(LOCAL_ONLY_LIVE_ACTIVITY_KINDS)는 앱 안에서만 갱신하므로 토큰을 받지 않는다
 */

/** ActivityKit 토큰은 16진수 글자다. 길이는 Apple 이 정하지 않아 넉넉히 둔다 */
const apnsTokenSchema = z
  .string()
  .trim()
  .regex(/^[0-9a-fA-F]{32,512}$/, 'Push token must be hex')
  .transform((t) => t.toLowerCase())

/** 서버가 푸시를 보내는 종류 (지금은 모든 종류) */
export const SERVER_LIVE_ACTIVITY_KINDS = [
  'generation',
  'quote',
  'sample',
] as const satisfies readonly LiveActivityKind[]
export type ServerLiveActivityKind = (typeof SERVER_LIVE_ACTIVITY_KINDS)[number]

const serverKindSchema = z.enum(SERVER_LIVE_ACTIVITY_KINDS)

export const liveActivityRegisterSchema = z.object({
  kind: serverKindSchema,
  refId: z.string().trim().min(1).max(100),
  pushToken: apnsTokenSchema,
}) satisfies z.ZodType<LiveActivityRegisterBody>

export const liveActivityStartTokenSchema = z.object({
  pushToStartToken: apnsTokenSchema,
}) satisfies z.ZodType<LiveActivityStartTokenBody>

export const liveActivityRefParamSchema = z.object({
  kind: serverKindSchema,
  refId: z.string().trim().min(1).max(100),
})

/**
 * APNs 'start' 푸시의 attributes-type. 앱 위젯 확장의 Swift ActivityAttributes 구조체 이름과 글자 그대로 같아야
 * 원격 시작(push-to-start)이 된다
 */
export const LIVE_ACTIVITY_ATTRIBUTES_TYPE = 'RecipeCatActivityAttributes'
